/**
 * The 1,000-acre programme block: one thousand marginal farmers, one acre
 * each, every plot's boundary drawn, walked one farm at a time.
 *
 * "Next" goes to the neighbouring plot (the block is ordered village by
 * village in a north-south snake), the arrow keys do the same, and the card
 * of the selected farmer - record, crops, water, VRA, crop advisory,
 * onboarding, WhatsApp - is the same card as everywhere else in the portal.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl, { type ExpressionSpecification, type Map as MapLibreMap } from 'maplibre-gl';
import type { Stage } from '@shared/model';
import { STAGES, STAGE_COLOR, STAGE_LABEL } from '@shared/model';
import { buildStyle } from '../map/basemap';
import { apiUrl, type Loadable } from '../api/client';
import { FarmerCard } from '../components/FarmerCard';
import { StagePill } from '../components/StagePill';
import { StateBlock } from '../components/StateBlock';
import { useRoster } from '../state/RosterContext';

export interface PlotProps {
  rank: number; holder_id: string; name: string; relation: string; village: string; khasras: string[]; pids: string[];
  area_acres: number; clipped: boolean; has_phone: boolean; holder_acres: number; farm_count: number; crop_last_season: string | null; pin: [number, number];
}
export interface BlockSummary { farmers: number; acres: number; acre_each: number; clipped_plots: number; with_phone: number; eligible_marginal_farmers: number; by_village: Record<string, number>; quota_rule: string; pick_rule: string; plot_rule: string; order_rule: string }

let cache: Promise<[GeoJSON.FeatureCollection, BlockSummary | null]> | null = null;
export function loadBlock(): Promise<[GeoJSON.FeatureCollection, BlockSummary | null]> {
  if (!cache) {
    cache = Promise.all([
      fetch(apiUrl('/data/roster/block1000.geojson')).then((r) => { if (!r.ok) throw new Error(`The block did not load (${r.status}). Run tools/build_block.py.`); return r.json() as Promise<GeoJSON.FeatureCollection>; }),
      fetch(apiUrl('/data/roster/block1000.json')).then((r) => (r.ok ? (r.json() as Promise<BlockSummary>) : null)).catch(() => null),
    ]);
    cache.catch(() => { cache = null; });
  }
  return cache;
}

const stageColour: ExpressionSpecification = ['match', ['coalesce', ['feature-state', 'stage'], 'target'], ...STAGES.flatMap((s) => [s, STAGE_COLOR[s]]), STAGE_COLOR.target] as unknown as ExpressionSpecification;
const empty = (): GeoJSON.FeatureCollection => ({ type: 'FeatureCollection', features: [] });

export function Block({ rank }: { rank: number | null }): JSX.Element {
  const { byId, rows, stageOf } = useRoster();
  const holder = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [data, setData] = useState<Loadable<GeoJSON.FeatureCollection>>({ status: 'loading' });
  const [summary, setSummary] = useState<BlockSummary | null>(null);
  const [ready, setReady] = useState(false);
  const [idx, setIdx] = useState(Math.max(0, (rank ?? 1) - 1));
  const [village, setVillage] = useState('all');
  const [q, setQ] = useState('');
  const [vra, setVra] = useState<GeoJSON.FeatureCollection | null>(null);

  useEffect(() => {
    let live = true;
    loadBlock().then(([fc, s]) => { if (!live) return; setData({ status: 'ready', data: fc }); setSummary(s); }).catch((e: Error) => live && setData({ status: 'error', message: e.message }));
    return () => { live = false; };
  }, []);
  useEffect(() => { if (rank) setIdx(Math.max(0, rank - 1)); }, [rank]);

  const plots = useMemo(() => (data.status === 'ready' ? data.data.features.map((f) => f.properties as unknown as PlotProps) : []), [data]);
  const plot: PlotProps | null = plots[idx] ?? null;
  const sel = plot ? byId.get(plot.holder_id) ?? null : null;

  // Keep the address in step so a farm can be shared as a link.
  useEffect(() => {
    if (!plot) return;
    const want = `#/block/${plot.rank}`;
    if (window.location.hash !== want) window.history.replaceState(null, '', want);
  }, [plot]);

  const go = (delta: number): void => setIdx((i) => Math.min(plots.length - 1, Math.max(0, i + delta)));
  useEffect(() => {
    const on = (e: KeyboardEvent): void => {
      if ((e.target as HTMLElement | null)?.tagName === 'INPUT' || (e.target as HTMLElement | null)?.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown') { go(1); e.preventDefault(); }
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') { go(-1); e.preventDefault(); }
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plots.length]);

  // ------------------------------------------------------------ the map
  useEffect(() => {
    if (!holder.current || mapRef.current || data.status !== 'ready') return;
    const map = new maplibregl.Map({ container: holder.current, style: buildStyle(null), center: [74.80, 24.58], zoom: 11.6, attributionControl: { compact: true } });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.on('load', () => {
      map.addSource('block', { type: 'geojson', data: data.data, promoteId: 'holder_id' });
      map.addLayer({ id: 'block-fill', type: 'fill', source: 'block', paint: { 'fill-color': stageColour, 'fill-opacity': ['case', ['boolean', ['feature-state', 'selected'], false], 0.7, 0.35] } });
      map.addLayer({ id: 'block-line', type: 'line', source: 'block', layout: { 'line-join': 'round' }, paint: { 'line-color': ['case', ['boolean', ['feature-state', 'selected'], false], '#ffffff', '#f2e9d8'], 'line-width': ['case', ['boolean', ['feature-state', 'selected'], false], 3, 1.2] } });
      map.addSource('vra', { type: 'geojson', data: empty() });
      map.addLayer({ id: 'vra-fill', type: 'fill', source: 'vra', paint: { 'fill-color': ['coalesce', ['get', 'colour'], '#888'], 'fill-opacity': 0.55 } });
      map.addLayer({ id: 'vra-line', type: 'line', source: 'vra', paint: { 'line-color': '#0b1014', 'line-width': 1 } });
      map.addSource('pins', { type: 'geojson', data: empty() });
      map.addLayer({ id: 'pins', type: 'circle', source: 'pins', paint: { 'circle-radius': 4, 'circle-color': '#fff', 'circle-stroke-color': '#0b1014', 'circle-stroke-width': 1.5 } });
      map.on('click', 'block-fill', (e) => {
        const r = Number((e.features?.[0]?.properties as { rank?: number } | undefined)?.rank);
        if (Number.isFinite(r)) setIdx(r - 1);
      });
      map.on('mouseenter', 'block-fill', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'block-fill', () => { map.getCanvas().style.cursor = ''; });
      setReady(true);
    });
    return () => { map.remove(); mapRef.current = null; setReady(false); };
  }, [data]);

  // Stage per plot and the selected one.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    for (const p of plots) map.setFeatureState({ source: 'block', id: p.holder_id }, { stage: stageOf(p.holder_id), selected: p.holder_id === plot?.holder_id });
    (map.getSource('pins') as maplibregl.GeoJSONSource | undefined)?.setData(plot ? { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: { type: 'Point', coordinates: [plot.pin[1], plot.pin[0]] }, properties: {} }] } : empty());
  }, [ready, plots, plot, stageOf, rows]);

  // Fly to the selected plot.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !plot) return;
    map.flyTo({ center: [plot.pin[1], plot.pin[0]], zoom: Math.max(map.getZoom(), 16.5), speed: 1.6 });
    setVra(null);
  }, [ready, plot]);
  useEffect(() => { (mapRef.current?.getSource('vra') as maplibregl.GeoJSONSource | undefined)?.setData(vra ?? empty()); }, [vra, ready]);

  const villages = useMemo(() => Object.keys(summary?.by_village ?? {}), [summary]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return plots.filter((p) => (village === 'all' || p.village === village) && (!s || p.name.toLowerCase().includes(s) || p.relation.toLowerCase().includes(s) || p.khasras.some((k) => k.toLowerCase() === s) || String(p.rank) === s));
  }, [plots, village, q]);
  const stageCounts = useMemo(() => {
    const c = Object.fromEntries(STAGES.map((s) => [s, 0])) as Record<Stage, number>;
    for (const p of plots) c[stageOf(p.holder_id)]++;
    return c;
  }, [plots, stageOf]);

  if (data.status !== 'ready') return <div className="farms-shell"><div style={{ padding: 20 }}><StateBlock state={data} title="Programme block" /></div></div>;

  return (
    <div className="farms-shell">
      <aside className="farms-side">
        <header className="farms-side__head">
          <h1>1,000-acre programme block</h1>
          <p>{summary ? `${summary.farmers.toLocaleString('en-IN')} marginal farmers · ${summary.acres.toLocaleString('en-IN')} acres · one acre each · ${villages.length} villages` : ''}</p>
        </header>

        <div className="block-nav">
          <button type="button" className="btn-line" disabled={idx <= 0} onClick={() => go(-1)}>← Previous</button>
          <div className="block-nav__pos">
            <strong>Farm {plot ? plot.rank.toLocaleString('en-IN') : '—'}</strong> of {plots.length.toLocaleString('en-IN')}
            {plot ? <div className="muted">{plot.village} · {plot.area_acres.toFixed(2)} ac{plot.clipped ? ' cut from' : ' ='} khasra {plot.khasras.join(' + ')}</div> : null}
          </div>
          <button type="button" className="btn-line" disabled={idx >= plots.length - 1} onClick={() => go(1)}>Next →</button>
        </div>

        <ul className="map-legend" style={{ marginBottom: 6 }}>
          {STAGES.map((s) => <li key={s}><i style={{ background: STAGE_COLOR[s] }} />{STAGE_LABEL[s]} <span className="muted">{stageCounts[s]}</span></li>)}
        </ul>

        <div className="farms-filters">
          <label>
            <span>Village</span>
            <select value={village} onChange={(e) => setVillage(e.target.value)}>
              <option value="all">All {villages.length}</option>
              {villages.map((v) => <option key={v} value={v}>{v} ({summary?.by_village[v]})</option>)}
            </select>
          </label>
          <label>
            <span>Find</span>
            <input className="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="name, father, khasra or farm no." />
          </label>
        </div>
        {q || village !== 'all' ? (
          <ul className="results" style={{ marginBottom: 8 }}>
            {list.slice(0, 40).map((p) => (
              <li key={p.holder_id} onClick={() => { setIdx(p.rank - 1); setQ(''); }}>
                <span><span className="muted">#{p.rank}</span> {p.name}{p.relation ? <span className="muted"> · {p.relation}</span> : null}</span>
                <span className="muted">{p.village}</span>
                <StagePill stage={stageOf(p.holder_id)} small />
              </li>
            ))}
            {!list.length ? <li className="muted">No farm matches.</li> : null}
          </ul>
        ) : null}

        {sel ? (
          <FarmerCard
            holder={sel}
            plot={plot ? { holder_id: plot.holder_id, rank: plot.rank, khasras: plot.khasras, pin: plot.pin, acres: plot.area_acres, clipped: plot.clipped } : undefined}
            onShowFarm={(_pid, pin) => mapRef.current?.flyTo({ center: [pin[1], pin[0]], zoom: 17 })}
            onZones={(fc) => { setVra(fc); if (fc && plot) mapRef.current?.flyTo({ center: [plot.pin[1], plot.pin[0]], zoom: 17.5 }); }}
          />
        ) : <p className="farms-hint">Pick a farm on the map or use Next.</p>}

        {summary ? (
          <footer className="farms-foot">
            <p className="farms-muted">{summary.quota_rule}. {summary.pick_rule}. {summary.plot_rule}. {summary.order_rule}. {summary.clipped_plots} of the {summary.farmers} plots are cut to one acre; the rest are whole khasras adding up to it. Picked from {summary.eligible_marginal_farmers.toLocaleString('en-IN')} eligible marginal farmers.</p>
          </footer>
        ) : null}
      </aside>
      <div ref={holder} className="farms-map" />
    </div>
  );
}
